/**
 * WebGPU-based 3D Neural Network Renderer
 * Drop-in replacement for PixiNetworkRendererClass using WebGPU for 3D visualization
 */

interface NodeInfo {
  id: string;
  layerName: string;
  nodeIndex: number;
  position: { x: number; y: number; z: number };
  activation: number;
  color: { r: number; g: number; b: number };
}

interface EdgeInfo {
  id: string;
  fromNode: string;
  toNode: string;
  weight: number;
  activation: number;
  fromPosition: { x: number; y: number; z: number };
  toPosition: { x: number; y: number; z: number };
}

interface LayerActivations {
  [layerName: string]: number[];
}

interface WeightChange {
  delta: number[];
  delta_normalized: number[];
  delta_shape: number[];
  delta_min: number;
  delta_max: number;
  delta_mean: number;
}

interface TrainingData {
  type: string;
  epoch: number;
  train_loss: number;
  val_loss: number;
  activations: LayerActivations;
  weights?: { [key: string]: number[][][] };
  weight_changes?: { [key: string]: WeightChange };
}

export class WebGPUNetworkRendererClass {
  // WebGPU resources
  private adapter: GPUAdapter | null = null;
  private device: GPUDevice | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private context: GPUCanvasContext | null = null;
  private format: GPUTextureFormat | null = null;

  // Rendering state
  private nodes: NodeInfo[] = [];
  private edges: EdgeInfo[] = [];
  private layerOrder: string[] = [];
  private layerNodeIndices: Map<string, number[]> = new Map();
  private layerStructureMap: Map<string, { spatial_dims: number[] }> = new Map(); // Store original structure dimensions
  private lastStructureHash: string = ''; // Track structure hash to prevent unnecessary recreation
  private loggedOutOfBoundsNodes?: Set<string>; // Track nodes that have logged out-of-bounds warnings
  private edgeToSourceNodeIndex: Map<number, number> = new Map(); // Map edge index to source node index for activation lookup

  // Buffers
  private nodeVertexBuffer: GPUBuffer | null = null;
  private nodeIndexBuffer: GPUBuffer | null = null;
  private nodeIndexCount = 0;
  private edgeVertexBuffer: GPUBuffer | null = null;
  private edgeVertexCount = 0;
  private nodeColorBuffer: GPUBuffer | null = null;
  private nodePositionBuffer: GPUBuffer | null = null;
  private edgeColorBuffer: GPUBuffer | null = null;
  private uniformBuffer: GPUBuffer | null = null;
  private depthTexture: GPUTexture | null = null;
  private commandEncoder: GPUCommandEncoder | null = null;

  // Pipeline
  private nodePipeline: GPURenderPipeline | null = null;
  private edgePipeline: GPURenderPipeline | null = null;
  private bindGroup: GPUBindGroup | null = null;
  private edgeBindGroup: GPUBindGroup | null = null;

  // Animation state
  private isAnimating = false;
  private animationStartTime = 0;
  private isBackpropMode = false;
  private backpropStartTime = 0;
  private isWaitingForNextEpoch = false;
  private animationDuration = 1000; // ms
  private endAnimationDelay = 500; // ms

  // Backprop visualization state
  private weightChanges: Map<string, WeightChange> = new Map();
  private showWeightDelta: boolean = true; // Toggle between delta (true) and absolute (false) values
  private previousWeightValues: Map<string, number[]> = new Map(); // Store previous epoch weights for absolute visualization

  // Camera/View
  private camera = {
    position: { x: 0, y: 0, z: 500 },
    target: { x: 0, y: 0, z: 0 },
    fov: 45,
    near: 0.1,
    far: 10000,
    rotationX: 0.4, // Better angle for 3D view
    rotationY: 0.6 + Math.PI, // Better angle for 3D view, rotated 180 degrees
    // Camera orientation (for FPS-style rotation)
    pitch: 0.4, // Vertical rotation (up/down)
    yaw: 0.6 + Math.PI,   // Horizontal rotation (left/right), rotated 180 degrees to view from start
  };

  private zoomLevel = 1.0;
  private isDragging = false;
  private isRightDragging = false;
  private lastMouseX = 0;
  private lastMouseY = 0;

  // State
  private currentTrainingData: TrainingData | null = null;
  private isInitialized = false;
  private initializationPromise: Promise<void>;
  private container: HTMLElement;

  // Animation tracking
  private nodeCount = 0;
  private edgeCount = 0;
  private lastNodeCount = 0;  // Track previous node count to preserve buffers
  private lastEpoch = -1;     // Track last epoch to detect epoch transitions

  // View mode state
  private viewMode: '3d_depth' | '2d_grid' = '3d_depth';
  private showEdges: boolean = true; // Toggle for showing/hiding connections
  private currentStructure: any = null;
  private currentCompactView: boolean = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.initializationPromise = this.initializeApp();
  }

  /**
   * Initialize WebGPU context and render setup
   */
  private async initializeApp(): Promise<void> {
    try {
      // Request GPU adapter and device
      const adapter = await navigator.gpu?.requestAdapter();
      if (!adapter) {
        throw new Error("WebGPU adapter not available");
      }
      this.adapter = adapter;

      this.device = await adapter.requestDevice();
      if (!this.device) {
        throw new Error("Failed to create WebGPU device");
      }

      // Create canvas
      this.canvas = document.createElement("canvas");
      // Ensure minimum size to avoid zero-size texture errors
      const width = Math.max(1, this.container.clientWidth || 800);
      const height = Math.max(1, this.container.clientHeight || 600);
      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.width = "100%";
      this.canvas.style.height = "100%";
      this.canvas.style.display = "block";
      this.container.appendChild(this.canvas);

      // Get context
      this.context = this.canvas.getContext("webgpu");
      if (!this.context) {
        throw new Error("Failed to get WebGPU context");
      }

      this.format = navigator.gpu!.getPreferredCanvasFormat();
      this.context.configure({
        device: this.device,
        format: this.format,
      });

      // Set up event listeners
      this.setupEventListeners();

      // Start render loop
      this.animate();

      this.isInitialized = true;
    } catch (error) {
      console.error("WebGPU initialization failed:", error);
      throw error;
    }
  }

  /**
   * Wait for async initialization to complete
   */
  public waitForInitialization(): Promise<void> {
    return this.initializationPromise;
  }

  /**
   * Set the view mode (3D depth or 2D grid)
   */
  public setViewMode(mode: '3d_depth' | '2d_grid'): void {
    if (this.viewMode === mode) {
      return; // No change needed
    }
    this.viewMode = mode;
    // Re-render with current structure if available
    if (this.currentStructure) {
      this.setNetworkStructure(this.currentStructure, this.currentCompactView);
    }
  }

  /**
   * Toggle edge visibility
   */
  public setShowEdges(show: boolean): void {
    this.showEdges = show;
  }

  /**
   * Toggle between showing weight delta vs absolute weight values in backprop visualization
   */
  public setShowWeightDelta(showDelta: boolean): void {
    this.showWeightDelta = showDelta;
    console.log(`[WebGPU Renderer] Weight visualization mode: ${showDelta ? 'delta (change)' : 'absolute'}`);
  }

  /**
   * Get list of layer names
   */
  public getLayerNames(): string[] {
    return [...this.layerOrder];
  }

  /**
   * Calculate bounding box for all nodes or a specific layer
   */
  private calculateBoundingBox(layerName?: string): { min: { x: number; y: number; z: number }, max: { x: number; y: number; z: number }, center: { x: number; y: number; z: number }, size: { x: number; y: number; z: number } } | null {
    if (this.nodes.length === 0) {
      return null;
    }

    let nodesToCheck: NodeInfo[];
    if (layerName) {
      const layerIndices = this.layerNodeIndices.get(layerName) || [];
      nodesToCheck = layerIndices.map(idx => this.nodes[idx]).filter(n => n !== undefined);
    } else {
      nodesToCheck = this.nodes;
    }

    if (nodesToCheck.length === 0) {
      return null;
    }

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    nodesToCheck.forEach(node => {
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      minZ = Math.min(minZ, node.position.z);
      maxX = Math.max(maxX, node.position.x);
      maxY = Math.max(maxY, node.position.y);
      maxZ = Math.max(maxZ, node.position.z);
    });

    const center = {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
      z: (minZ + maxZ) / 2
    };

    const size = {
      x: maxX - minX,
      y: maxY - minY,
      z: maxZ - minZ
    };

    return {
      min: { x: minX, y: minY, z: minZ },
      max: { x: maxX, y: maxY, z: maxZ },
      center,
      size
    };
  }

  /**
   * Zoom to fit all nodes (initial view)
   */
  public zoomToFitAll(): void {
    const bbox = this.calculateBoundingBox();
    if (!bbox) return;

    this.zoomToBoundingBox(bbox);
  }

  /**
   * Teleport camera to view a specific layer
   */
  public zoomToLayer(layerName: string): void {
    const bbox = this.calculateBoundingBox(layerName);
    if (!bbox) {
      console.warn(`[WebGPU Renderer] Layer "${layerName}" not found or has no nodes`);
      return;
    }

    this.zoomToBoundingBox(bbox);
  }

  /**
   * Zoom camera to fit a bounding box
   */
  private zoomToBoundingBox(bbox: { center: { x: number; y: number; z: number }, size: { x: number; y: number; z: number } }): void {
    // Calculate the maximum dimension to ensure everything fits
    const maxSize = Math.max(bbox.size.x, bbox.size.y, bbox.size.z);
    
    // Add padding (20% on each side)
    const padding = maxSize * 0.2;
    const totalSize = maxSize + padding * 2;
    
    // Calculate distance needed to fit the bounding box in view
    // Using FOV and trigonometry to calculate required distance
    const fovRad = (this.camera.fov * Math.PI) / 180;
    const distance = (totalSize / 2) / Math.tan(fovRad / 2);
    
    // Add some extra distance for better view
    const cameraDistance = distance * 1.2;
    
    // Position camera at a good viewing angle (45 degrees up and around)
    // Rotated 180 degrees to view from the start of the network (input layer)
    const angleY = Math.PI / 4; // 45 degrees
    const angleX = Math.PI / 4 + Math.PI; // 45 degrees + 180 degrees rotation
    
    this.camera.position.x = bbox.center.x + cameraDistance * Math.sin(angleX) * Math.cos(angleY);
    this.camera.position.y = bbox.center.y + cameraDistance * Math.sin(angleY);
    this.camera.position.z = bbox.center.z + cameraDistance * Math.cos(angleX) * Math.cos(angleY);
    
    // Point camera at the center of the bounding box
    this.camera.target.x = bbox.center.x;
    this.camera.target.y = bbox.center.y;
    this.camera.target.z = bbox.center.z;
    
    // Update camera rotation to look at target
    const dx = this.camera.target.x - this.camera.position.x;
    const dy = this.camera.target.y - this.camera.position.y;
    const dz = this.camera.target.z - this.camera.position.z;
    
    const distanceToTarget = Math.sqrt(dx * dx + dy * dy + dz * dz);
    this.camera.pitch = Math.asin(dy / distanceToTarget);
    this.camera.yaw = Math.atan2(dx, dz);
    
    // Update zoom level
    this.zoomLevel = Math.max(0.1, Math.min(3.0, 500 / cameraDistance));
  }

  /**
   * Set the network structure and create visualization
   */
  public setNetworkStructure(structure: any, compactView: boolean = false): void {
    if (!this.isInitialized) {
      console.warn("Renderer not initialized");
      return;
    }

    // Create hash of structure to detect actual changes
    // Include structure content, compactView, and viewMode in hash
    const structureHash = JSON.stringify({ 
      structure: structure, 
      compactView: compactView, 
      viewMode: this.viewMode 
    });
    
    // If structure hasn't changed and nodes already exist, don't recreate them
    if (this.lastStructureHash === structureHash && this.nodes.length > 0) {
      console.log('[WebGPU Renderer] Structure unchanged (hash match), skipping node recreation');
      console.log('[WebGPU Renderer] Current hash:', this.lastStructureHash.substring(0, 100) + '...');
      console.log('[WebGPU Renderer] New hash:', structureHash.substring(0, 100) + '...');
      console.log('[WebGPU Renderer] Node count:', this.nodes.length);
      return;
    }

    // Log when recreation is happening
    if (this.nodes.length > 0) {
      console.warn('[WebGPU Renderer] WARNING: Recreating nodes! Previous hash:', this.lastStructureHash.substring(0, 100));
      console.warn('[WebGPU Renderer] WARNING: New hash:', structureHash.substring(0, 100));
      console.warn('[WebGPU Renderer] WARNING: This should not happen between epochs!');
    }

    // Store structure and compactView for view mode changes
    this.currentStructure = structure;
    this.currentCompactView = compactView;
    this.lastStructureHash = structureHash;

    console.log(`[WebGPU Renderer] setNetworkStructure called with compactView=${compactView}, viewMode=${this.viewMode}`);
    console.log('[WebGPU Renderer] Creating nodes (this should only happen once at initialization)');
    
    this.nodes = [];
    this.edges = [];
    this.layerOrder = [];
    this.layerNodeIndices.clear();
    this.layerStructureMap.clear(); // Clear structure map
    this.loggedOutOfBoundsNodes = new Set(); // Reset logged nodes when structure changes
    this.edgeToSourceNodeIndex.clear(); // Clear edge mapping

    const layers = structure.layers || [];
    
    // Store original structure dimensions for each layer (before downsampling)
    layers.forEach((layer: any) => {
      if (layer.name && layer.spatial_dims) {
        this.layerStructureMap.set(layer.name, {
          spatial_dims: layer.spatial_dims
        });
      }
    });
    
    const layerSpacing = 200;
    const compactLayerSpacing = 100; // Tighter spacing in compact mode
    const nodeSpacing = 8; // Smaller spacing for 3D grids
    const channelSpacing = 50; // Spacing between channels in conv layers
    
    // Get input image size if available
    const inputImageSize = structure.spatial_info?.input_image_size || null;

    // Create nodes with 3D spatial arrangement
    let nodeIdCounter = 0;
    let cumulativeZDepth = 0; // Track cumulative Z-depth to account for collapsed channels (for 3D depth mode)
    let cumulativeYDepth = 0; // Track cumulative Y-depth for 2D grid mode (negative Y = up)
    
    layers.forEach((layer: any, layerIndex: number) => {
      const layerCategory = layer.layer_category || this.detectLayerCategory(layer, layerIndex);
      const layerName = layer.name || `layer_${layerIndex}`;
      this.layerOrder.push(layerName);
      this.layerNodeIndices.set(layerName, []);

      // Base position for this layer (depends on view mode)
      let layerBaseZ = 0;
      let layerBaseY = 0;
      
      if (this.viewMode === '2d_grid') {
        // 2D Grid mode: layers stacked on Y-axis (negative = up)
        layerBaseY = -layerIndex * layerSpacing;
        layerBaseZ = 0; // Fixed Z for 2D grid view
      } else {
        // 3D Depth mode: layers stacked on Z-axis
        layerBaseZ = cumulativeZDepth;
        layerBaseY = 0; // Y is used for rows within layers
      }

      // Position nodes based on layer type
      if (layerCategory === 'input_conv' && layer.spatial_dims) {
        // Input layer: 2D grid matching image dimensions
        const [height, width] = layer.spatial_dims;
        const totalNodes = height * width;
        
        for (let row = 0; row < height; row++) {
          for (let col = 0; col < width; col++) {
            const nodeIndex = row * width + col;
            let x: number, y: number, z: number;
            
            if (this.viewMode === '2d_grid') {
              // 2D Grid mode: X for columns, Y for layer position + rows, Z fixed
              x = (col - width / 2) * nodeSpacing;
              y = layerBaseY - (row - height / 2) * nodeSpacing; // Negative Y = up
              z = 0;
            } else {
              // 3D Depth mode: X for columns, Y for rows, Z for layer depth
              x = (col - width / 2) * nodeSpacing;
              y = (height / 2 - row) * nodeSpacing; // Flip Y to match image orientation
              z = layerBaseZ;
            }
            
            const nodeId = `node_${nodeIdCounter}`;
            nodeIdCounter++;

            const node: NodeInfo = {
              id: nodeId,
              layerName,
              nodeIndex: nodeIndex,
              position: { x, y, z },
              activation: 0,
              color: { r: 0.33, g: 0.33, b: 0.33 },
            };

            this.nodes.push(node);
            this.layerNodeIndices.get(layerName)!.push(this.nodes.length - 1);
          }
        }
        // Update cumulative depth based on view mode
        if (this.viewMode === '2d_grid') {
          cumulativeYDepth = layerBaseY - layerSpacing; // Next layer goes up (more negative)
        } else {
          cumulativeZDepth = layerBaseZ + layerSpacing;
        }
        console.log(`[WebGPU Renderer] Layer ${layerIndex} [INPUT_CONV] ${layerName}: ${height}x${width} (${totalNodes} nodes)`);
      } else if (layerCategory === 'conv' && layer.spatial_dims && layer.out_channels && layerCategory !== 'input_conv') {
        // Conv layer: 3D arrangement (channels × height × width)
        const [height, width] = layer.spatial_dims;
        const channels = layer.out_channels;
        
        // Determine which channels to show (compactView only controls how many, not layout)
        let channelsToShow: number[] = [];
        if (compactView) {
          // Compact view: only show first and last channel
          if (channels > 0) {
            channelsToShow = [0, channels - 1];
          }
        } else {
          // Full view: show all channels
          channelsToShow = Array.from({ length: channels }, (_, i) => i);
        }
        
        if (this.viewMode === '2d_grid') {
          // 2D Grid mode: channels side-by-side on X-axis, rows on Y-axis
          for (let chIdx = 0; chIdx < channelsToShow.length; chIdx++) {
            const ch = channelsToShow[chIdx];
            for (let row = 0; row < height; row++) {
              for (let col = 0; col < width; col++) {
                const nodeIndex = ch * height * width + row * width + col;
                
                // X-axis: channels spread horizontally + columns
                const x = (chIdx - channelsToShow.length / 2) * channelSpacing + (col - width / 2) * nodeSpacing;
                // Y-axis: layer position + rows (negative Y = up)
                const y = layerBaseY - (row - height / 2) * nodeSpacing;
                // Z-axis: fixed at 0
                const z = 0;
                
                const nodeId = `node_${nodeIdCounter}`;
                nodeIdCounter++;

                const node: NodeInfo = {
                  id: nodeId,
                  layerName,
                  nodeIndex: nodeIndex,
                  position: { x, y, z },
                  activation: 0,
                  color: { r: 0.33, g: 0.33, b: 0.33 },
                };

                this.nodes.push(node);
                this.layerNodeIndices.get(layerName)!.push(this.nodes.length - 1);
              }
            }
          }
          // Update cumulative Y depth for next layer
          cumulativeYDepth = layerBaseY - layerSpacing;
        } else {
          // 3D Depth mode: channels arranged in a 2D grid pattern with spacing between each channel's spatial grid
          // Calculate grid dimensions for channels (arrange channels in a roughly square grid)
          const channelsPerRow = Math.ceil(Math.sqrt(channelsToShow.length));
          const channelGridWidth = width * nodeSpacing;
          const channelGridHeight = height * nodeSpacing;
          const spacingBetweenChannels = Math.max(channelGridWidth, channelGridHeight) * 1.5; // Space between channel grids
          
          for (let chIdx = 0; chIdx < channelsToShow.length; chIdx++) {
            const ch = channelsToShow[chIdx];
            // Calculate grid position for this channel
            const channelGridRow = Math.floor(chIdx / channelsPerRow);
            const channelGridCol = chIdx % channelsPerRow;
            
            for (let row = 0; row < height; row++) {
              for (let col = 0; col < width; col++) {
                const nodeIndex = ch * height * width + row * width + col;
                
                // X-axis: channel grid column offset + column within channel grid
                const channelGridXOffset = (channelGridCol - (channelsPerRow - 1) / 2) * spacingBetweenChannels;
                const x = channelGridXOffset + (col - width / 2) * nodeSpacing;
                
                // Y-axis: channel grid row offset + row within channel grid
                const channelGridYOffset = (channelGridRow - (Math.ceil(channelsToShow.length / channelsPerRow) - 1) / 2) * spacingBetweenChannels;
                const y = channelGridYOffset + (height / 2 - row) * nodeSpacing;
                
                // Z-axis: layer base position (all channels at same depth)
                const z = layerBaseZ;
                
                const nodeId = `node_${nodeIdCounter}`;
                nodeIdCounter++;

                const node: NodeInfo = {
                  id: nodeId,
                  layerName,
                  nodeIndex: nodeIndex,
                  position: { x, y, z },
                  activation: 0,
                  color: { r: 0.33, g: 0.33, b: 0.33 },
                };

                this.nodes.push(node);
                this.layerNodeIndices.get(layerName)!.push(this.nodes.length - 1);
              }
            }
          }
          // Update cumulative depth: layer base + spacing to next layer (channels are at same Z depth)
          cumulativeZDepth = layerBaseZ + layerSpacing;
        }
        const totalNodes = channelsToShow.length * height * width;
        console.log(`[WebGPU Renderer] Layer ${layerIndex} [CONV] ${layerName}: ${channels}ch x ${height}x${width} (${totalNodes} nodes, showing ${channelsToShow.length} channels)`);
      } else {
        // FC layer or other: 3D grid arrangement
        const nodeCount = this.getNodeCount(layer);
        const gridSize = Math.ceil(Math.cbrt(nodeCount));
        
        for (let i = 0; i < nodeCount; i++) {
          let x: number, y: number, z: number;
          
          if (this.viewMode === '2d_grid') {
            // 2D Grid mode: arrange in 2D grid, centered at layer Y position
            const grid2DSize = Math.ceil(Math.sqrt(nodeCount));
            const gridX = i % grid2DSize;
            const gridY = Math.floor(i / grid2DSize);
            x = (gridX - grid2DSize / 2) * nodeSpacing;
            y = layerBaseY - (gridY - grid2DSize / 2) * nodeSpacing; // Negative Y = up
            z = 0;
          } else {
            // 3D Depth mode: arrange in 3D grid
            x = (i % gridSize - gridSize / 2) * nodeSpacing;
            y = (Math.floor(i / gridSize) % gridSize - gridSize / 2) * nodeSpacing;
            const zOffset = (Math.floor(i / (gridSize * gridSize)) - gridSize / 2) * nodeSpacing;
            y = y + zOffset;
            z = layerBaseZ;
          }
          
          const nodeId = `node_${nodeIdCounter}`;
          nodeIdCounter++;

          const node: NodeInfo = {
            id: nodeId,
            layerName,
            nodeIndex: i,
            position: { x, y, z },
            activation: 0,
            color: { r: 0.33, g: 0.33, b: 0.33 },
          };

          this.nodes.push(node);
          this.layerNodeIndices.get(layerName)!.push(this.nodes.length - 1);
        }
        // Update cumulative depth based on view mode
        if (this.viewMode === '2d_grid') {
          cumulativeYDepth = layerBaseY - layerSpacing; // Next layer goes up (more negative)
        } else {
          cumulativeZDepth = layerBaseZ + layerSpacing;
        }
        const categoryUpper = layerCategory.toUpperCase();
        console.log(`[WebGPU Renderer] Layer ${layerIndex} [${categoryUpper}] ${layerName}: ${nodeCount} nodes`);
      }
    });

    // Create edges between consecutive visible layers (only in 3D depth mode)
    this.edgeToSourceNodeIndex.clear(); // Clear previous mapping
    if (this.viewMode === '3d_depth') {
      for (let i = 0; i < this.layerOrder.length - 1; i++) {
        const fromLayer = this.layerOrder[i];
        const toLayer = this.layerOrder[i + 1];
        const fromIndices = this.layerNodeIndices.get(fromLayer) || [];
        const toIndices = this.layerNodeIndices.get(toLayer) || [];

        let edgeIdCounter = 0;
        fromIndices.forEach((fromIdx) => {
          toIndices.forEach((toIdx) => {
            const fromNode = this.nodes[fromIdx];
            const toNode = this.nodes[toIdx];

            const edge: EdgeInfo = {
              id: `edge_${edgeIdCounter}`,
              fromNode: fromNode.id,
              toNode: toNode.id,
              weight: 0,
              activation: 0,
              fromPosition: { ...fromNode.position },
              toPosition: { ...toNode.position },
            };

            const edgeIndex = this.edges.length;
            this.edges.push(edge);
            // Store mapping from edge index to source node index for activation lookup
            this.edgeToSourceNodeIndex.set(edgeIndex, fromIdx);
            edgeIdCounter++;
          });
        });
      }
    }
    // Skip edge creation in 2D grid mode

    this.nodeCount = this.nodes.length;
    this.edgeCount = this.edges.length;

    // Create GPU buffers and pipelines
    // Pass preserveColorBuffers=true to avoid flickering when node count is unchanged
    const shouldPreserveColorBuffers = this.lastNodeCount === this.nodeCount && this.lastNodeCount > 0;
    this.createBuffersAndPipelines(shouldPreserveColorBuffers);

    // Update tracked counts for next structure change
    this.lastNodeCount = this.nodeCount;

    console.log(`[WebGPU Renderer] Network structure set: ${this.nodes.length} nodes, ${this.edges.length} edges`);
    
    // Auto-zoom to fit entire network on initial load
    this.zoomToFitAll();
  }

  /**
   * Get node count from layer configuration
   */
  private getNodeCount(layer: any): number {
    // For conv layers with spatial dims, calculate total nodes
    if (layer.spatial_dims && layer.out_channels) {
      const [height, width] = layer.spatial_dims;
      return height * width * layer.out_channels;
    }
    
    // For input conv with spatial dims
    if (layer.spatial_dims && layer.layer_category === 'input_conv') {
      const [height, width] = layer.spatial_dims;
      return height * width;
    }
    
    // Fallback to standard parameters
    return (
      layer.out_features ||
      layer.parameters?.out_features ||
      layer.parameters?.output_size ||
      layer.parameters?.num_features ||
      layer.parameters?.filters ||
      layer.parameters?.channels ||
      layer.out_channels ||
      10 // default fallback
    );
  }

  /**
   * Detect layer category from layer info
   */
  private detectLayerCategory(layer: any, layerIndex: number): string {
    if (layer.layer_category) {
      return layer.layer_category;
    }
    
    const layerType = layer.type?.toLowerCase() || '';
    if (layerType.includes('conv2d') && layerIndex === 0) {
      return 'input_conv';
    } else if (layerType.includes('conv')) {
      return 'conv';
    } else if (layerType.includes('linear') || layerType.includes('dense')) {
      return 'fc';
    } else {
      return 'other';
    }
  }

  /**
   * Create GPU buffers and render pipelines
   * @param preserveColorBuffers - If true, preserve color buffers to avoid flicker between epochs
   */
  private createBuffersAndPipelines(preserveColorBuffers: boolean = false): void {
    if (!this.device || !this.canvas) {
      console.error("Device or canvas not initialized");
      return;
    }

    // Destroy existing buffers and textures before creating new ones
    // Pass preserveColorBuffers to avoid flicker when structure hasn't changed meaningfully
    this.destroyBuffers(preserveColorBuffers);

    // Create depth texture only if canvas has valid size
    if (this.canvas.width > 0 && this.canvas.height > 0) {
      this.depthTexture = this.device.createTexture({
        size: [this.canvas.width, this.canvas.height],
        format: "depth24plus",
        usage: GPUTextureUsage.RENDER_ATTACHMENT,
      });
    } else {
      // Canvas size is zero, skip depth texture creation
      console.warn("[WebGPU] Canvas size is zero, skipping depth texture creation");
      this.depthTexture = null;
    }

    // Create vertex buffers for nodes (cube geometry)
    const nodeVertexData = this.createNodeVertices();
    this.nodeVertexBuffer = this.device.createBuffer({
      label: "node vertex buffer",
      size: nodeVertexData.vertices.byteLength,
      mappedAtCreation: true,
      usage: GPUBufferUsage.VERTEX,
    });
    new Float32Array(this.nodeVertexBuffer.getMappedRange()).set(
      nodeVertexData.vertices
    );
    this.nodeVertexBuffer.unmap();

    this.nodeIndexBuffer = this.device.createBuffer({
      label: "node index buffer",
      size: nodeVertexData.indices.byteLength,
      mappedAtCreation: true,
      usage: GPUBufferUsage.INDEX,
    });
    new Uint32Array(this.nodeIndexBuffer.getMappedRange()).set(
      nodeVertexData.indices
    );
    this.nodeIndexBuffer.unmap();
    this.nodeIndexCount = nodeVertexData.indices.length;

    // Create vertex buffers for edges (line segments) - only if edges exist
    if (this.edgeCount > 0) {
      const edgeVertexData = this.createEdgeVertices();
      this.edgeVertexBuffer = this.device.createBuffer({
        label: "edge vertex buffer",
        size: edgeVertexData.byteLength,
        mappedAtCreation: true,
        usage: GPUBufferUsage.VERTEX,
      });
      new Float32Array(this.edgeVertexBuffer.getMappedRange()).set(
        edgeVertexData
      );
      this.edgeVertexBuffer.unmap();
      this.edgeVertexCount = edgeVertexData.length / 3;
    } else {
      // No edges in 2D grid mode
      this.edgeVertexBuffer = null;
      this.edgeVertexCount = 0;
    }

    // Create node position buffer (for instancing)
    const nodePositions = new Float32Array(this.nodeCount * 4);
    this.nodes.forEach((node, idx) => {
      nodePositions[idx * 4] = node.position.x;
      nodePositions[idx * 4 + 1] = node.position.y;
      nodePositions[idx * 4 + 2] = node.position.z;
      nodePositions[idx * 4 + 3] = 1.0;
    });

    this.nodePositionBuffer = this.device.createBuffer({
      label: "node position buffer",
      size: nodePositions.byteLength,
      mappedAtCreation: true,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    new Float32Array(this.nodePositionBuffer.getMappedRange()).set(
      nodePositions
    );
    this.nodePositionBuffer.unmap();

    // Create color buffers
    // If we preserved the color buffer, skip recreation to avoid flicker
    if (!preserveColorBuffers || !this.nodeColorBuffer) {
      const nodeColors = new Float32Array(this.nodeCount * 4);

      for (let i = 0; i < this.nodeCount; i++) {
        nodeColors[i * 4] = 0.33;
        nodeColors[i * 4 + 1] = 0.33;
        nodeColors[i * 4 + 2] = 0.33;
        nodeColors[i * 4 + 3] = 1.0;
      }

      this.nodeColorBuffer = this.device.createBuffer({
        label: "node color buffer",
        size: nodeColors.byteLength,
        mappedAtCreation: true,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      });
      new Float32Array(this.nodeColorBuffer.getMappedRange()).set(nodeColors);
      this.nodeColorBuffer.unmap();
    } else {
      // Preserved color buffer - just reset colors to neutral gray
      console.log('[WebGPU Renderer] Preserving color buffer across structure update');
      this.resetColorsToNeutral();
    }

    // Create edge color buffer - only if edges exist
    if (this.edgeCount > 0) {
      const edgeColors = new Float32Array(this.edgeCount * 4);
      for (let i = 0; i < this.edgeCount; i++) {
        // Initialize to white with good visibility
        edgeColors[i * 4] = 1.0;     // R
        edgeColors[i * 4 + 1] = 1.0; // G
        edgeColors[i * 4 + 2] = 1.0; // B
        edgeColors[i * 4 + 3] = 0.8; // A
      }

      this.edgeColorBuffer = this.device.createBuffer({
        label: "edge color buffer",
        size: edgeColors.byteLength,
        mappedAtCreation: true,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      });
      new Float32Array(this.edgeColorBuffer.getMappedRange()).set(edgeColors);
      this.edgeColorBuffer.unmap();
    } else {
      // No edges in 2D grid mode
      this.edgeColorBuffer = null;
    }

    // Create uniform buffer for MVP matrix and other uniforms (16 * 4 bytes for matrix + 4 * 4 for camera data)
    this.uniformBuffer = this.device.createBuffer({
      label: "uniform buffer",
      size: 256,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.createPipelines();
  }

  /**
   * Create node geometry vertices (unit cube) with indices
   */
  private createNodeVertices(): { vertices: Float32Array; indices: Uint32Array } {
    // Simple cube geometry for nodes (-0.5 to 0.5)
    const vertices = new Float32Array([
      // Front face
      -0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5,
      // Back face
      -0.5, -0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, -0.5, -0.5,
      // Top face
      -0.5, 0.5, -0.5, -0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5,
      // Bottom face
      -0.5, -0.5, -0.5, 0.5, -0.5, -0.5, 0.5, -0.5, 0.5, -0.5, -0.5, 0.5,
      // Right face
      0.5, -0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, -0.5, 0.5,
      // Left face
      -0.5, -0.5, -0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, -0.5,
    ]);

    const indices = new Uint32Array([
      0, 1, 2, 0, 2, 3, // front
      4, 6, 5, 4, 7, 6, // back
      8, 9, 10, 8, 10, 11, // top
      12, 15, 14, 12, 14, 13, // bottom
      16, 17, 18, 16, 18, 19, // right
      20, 22, 21, 20, 23, 22, // left
    ]);

    return { vertices, indices };
  }

  /**
   * Create edge geometry vertices (line segments)
   */
  private createEdgeVertices(): Float32Array {
    const vertices: number[] = [];
    this.edges.forEach((edge) => {
      // Start point
      vertices.push(edge.fromPosition.x, edge.fromPosition.y, edge.fromPosition.z);
      // End point
      vertices.push(edge.toPosition.x, edge.toPosition.y, edge.toPosition.z);
    });
    return new Float32Array(vertices);
  }

  /**
   * Create render pipelines
   */
  private createPipelines(): void {
    if (!this.device || !this.format) {
      console.error("Device or format not initialized");
      return;
    }

    const nodeShaderCode = this.getNodeShaderCode();
    const edgeShaderCode = this.getEdgeShaderCode();

    const nodeShaderModule = this.device.createShaderModule({
      label: "node shader",
      code: nodeShaderCode,
    });

    const edgeShaderModule = this.device.createShaderModule({
      label: "edge shader",
      code: edgeShaderCode,
    });

    // Create bind group layout for nodes
    const nodeBindGroupLayout = this.device.createBindGroupLayout({
      label: "node bind group layout",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "uniform" as GPUBufferBindingType },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "read-only-storage" as GPUBufferBindingType },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "read-only-storage" as GPUBufferBindingType },
        },
      ] as GPUBindGroupLayoutEntry[],
    });

    // Create node bind group
    this.bindGroup = this.device.createBindGroup({
      label: "node bind group",
      layout: nodeBindGroupLayout,
      entries: [
        {
          binding: 0,
          resource: { buffer: this.uniformBuffer! },
        },
        {
          binding: 1,
          resource: { buffer: this.nodeColorBuffer! },
        },
        {
          binding: 2,
          resource: { buffer: this.nodePositionBuffer! },
        },
      ],
    });

    const nodePipelineLayout = this.device.createPipelineLayout({
      label: "node pipeline layout",
      bindGroupLayouts: [nodeBindGroupLayout],
    });

    // Create edge bind group layout (simpler, no positions or instancing)
    const edgeBindGroupLayout = this.device.createBindGroupLayout({
      label: "edge bind group layout",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "uniform" as GPUBufferBindingType },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "read-only-storage" as GPUBufferBindingType },
        },
      ] as GPUBindGroupLayoutEntry[],
    });

    const edgePipelineLayout = this.device.createPipelineLayout({
      label: "edge pipeline layout",
      bindGroupLayouts: [edgeBindGroupLayout],
    });

    // Create edge bind group - only if edges exist
    if (this.edgeCount > 0 && this.edgeColorBuffer) {
      this.edgeBindGroup = this.device.createBindGroup({
        label: "edge bind group",
        layout: edgeBindGroupLayout,
        entries: [
          {
            binding: 0,
            resource: { buffer: this.uniformBuffer! },
          },
          {
            binding: 1,
            resource: { buffer: this.edgeColorBuffer },
          },
        ],
      });
    } else {
      // No edges in 2D grid mode
      this.edgeBindGroup = null;
    }

    // Create node render pipeline
    this.nodePipeline = this.device.createRenderPipeline({
      label: "node render pipeline",
      layout: nodePipelineLayout,
      primitive: {
        topology: "triangle-list",
        cullMode: "none", // Render both front and back faces for solid appearance
      },
      vertex: {
        module: nodeShaderModule,
        entryPoint: "vertexMain",
        buffers: [
          {
            arrayStride: 3 * 4,
            attributes: [
              {
                shaderLocation: 0,
                offset: 0,
                format: "float32x3" as GPUVertexFormat,
              },
            ],
          },
        ] as GPUVertexBufferLayout[],
      },
      fragment: {
        module: nodeShaderModule,
        entryPoint: "fragmentMain",
        targets: [
          {
            format: this.format,
            blend: {
              color: { srcFactor: "src-alpha" as GPUBlendFactor, dstFactor: "one-minus-src-alpha" as GPUBlendFactor, operation: "add" as GPUBlendOperation },
              alpha: { srcFactor: "one" as GPUBlendFactor, dstFactor: "one-minus-src-alpha" as GPUBlendFactor, operation: "add" as GPUBlendOperation },
            },
          },
        ] as GPUColorTargetState[],
      },
      depthStencil: {
        depthWriteEnabled: true,
        depthCompare: "less",
        format: "depth24plus",
      },
    });

    // Create edge render pipeline
    this.edgePipeline = this.device.createRenderPipeline({
      label: "edge render pipeline",
      layout: edgePipelineLayout,
      primitive: {
        topology: "line-list",
        cullMode: "none",
      },
      vertex: {
        module: edgeShaderModule,
        entryPoint: "vertexMain",
        buffers: [
          {
            arrayStride: 3 * 4,
            attributes: [
              {
                shaderLocation: 0,
                offset: 0,
                format: "float32x3" as GPUVertexFormat,
              },
            ],
          },
        ] as GPUVertexBufferLayout[],
      },
      fragment: {
        module: edgeShaderModule,
        entryPoint: "fragmentMain",
        targets: [
          {
            format: this.format,
            blend: {
              color: { srcFactor: "src-alpha" as GPUBlendFactor, dstFactor: "one-minus-src-alpha" as GPUBlendFactor, operation: "add" as GPUBlendOperation },
              alpha: { srcFactor: "one" as GPUBlendFactor, dstFactor: "one-minus-src-alpha" as GPUBlendFactor, operation: "add" as GPUBlendOperation },
            },
          },
        ] as GPUColorTargetState[],
      },
      depthStencil: {
        depthWriteEnabled: true,
        depthCompare: "less",
        format: "depth24plus",
      },
    });
  }

  /**
   * Get node shader code (WGSL)
   */
  private getNodeShaderCode(): string {
    return `
      struct Uniforms {
        projMatrix: mat4x4<f32>,
        viewMatrix: mat4x4<f32>,
        time: f32,
        padding: f32,
      };

      @binding(0) @group(0) var<uniform> uniforms: Uniforms;
      @binding(1) @group(0) var<storage, read> colors: array<vec4<f32>>;
      @binding(2) @group(0) var<storage, read> positions: array<vec4<f32>>;

      struct VertexInput {
        @location(0) position: vec3<f32>,
        @builtin(vertex_index) vertexIndex: u32,
        @builtin(instance_index) instanceIndex: u32,
      };

      struct VertexOutput {
        @builtin(position) position: vec4<f32>,
        @location(0) color: vec4<f32>,
        @location(1) normal: vec3<f32>,
      };

      @vertex
      fn vertexMain(input: VertexInput) -> VertexOutput {
        let scale = 5.0;
        var output: VertexOutput;

        // Get node world position from storage
        let nodeWorldPos = positions[input.instanceIndex].xyz;
        let vertexLocalPos = input.position * scale;
        let vertexWorldPos = nodeWorldPos + vertexLocalPos;

        // Transform to clip space
        let viewPos = uniforms.viewMatrix * vec4<f32>(vertexWorldPos, 1.0);
        output.position = uniforms.projMatrix * viewPos;
        output.color = colors[input.instanceIndex];
        output.normal = input.position; // Simplified normal (cube's vertex position)

        return output;
      }

      @fragment
      fn fragmentMain(input: VertexOutput) -> @location(0) vec4<f32> {
        // Simple lighting: normal * light direction
        let light = normalize(vec3<f32>(1.0, 1.0, 1.0));
        let brightness = max(0.3, dot(normalize(input.normal), light));
        return input.color * vec4<f32>(brightness, brightness, brightness, 1.0);
      }
    `;
  }

  /**
   * Get edge shader code (WGSL)
   */
  private getEdgeShaderCode(): string {
    return `
      struct Uniforms {
        projMatrix: mat4x4<f32>,
        viewMatrix: mat4x4<f32>,
        time: f32,
        padding: f32,
      };

      @binding(0) @group(0) var<uniform> uniforms: Uniforms;
      @binding(1) @group(0) var<storage, read> colors: array<vec4<f32>>;

      struct VertexInput {
        @location(0) position: vec3<f32>,
        @builtin(vertex_index) vertexIndex: u32,
      };

      struct VertexOutput {
        @builtin(position) position: vec4<f32>,
        @location(0) color: vec4<f32>,
      };

      @vertex
      fn vertexMain(input: VertexInput) -> VertexOutput {
        var output: VertexOutput;
        let viewPos = uniforms.viewMatrix * vec4<f32>(input.position, 1.0);
        output.position = uniforms.projMatrix * viewPos;
        // Each edge has 2 vertices (start and end) in line-list topology
        // So edge index = vertex_index / 2 (integer division)
        let edgeIndex = input.vertexIndex / 2u;
        output.color = colors[edgeIndex];
        return output;
      }

      @fragment
      fn fragmentMain(input: VertexOutput) -> @location(0) vec4<f32> {
        return input.color;
      }
    `;
  }

  /**
   * Update training data and trigger animation (for epoch transitions)
   */
  public updateTrainingData(data: TrainingData): void {
    // Store weight changes for backprop visualization
    if (data.weight_changes) {
      this.weightChanges.clear();
      Object.entries(data.weight_changes).forEach(([layerName, changeData]) => {
        this.weightChanges.set(layerName, changeData);
      });
      console.log(`[WebGPU Renderer] Received weight changes for ${this.weightChanges.size} layers`);
    }

    // Detect epoch transitions and reset colors to provide visual continuity
    if (this.lastEpoch !== -1 && data.epoch !== this.lastEpoch) {
      console.log(`[WebGPU Renderer] Epoch transition detected: ${this.lastEpoch} → ${data.epoch}, resetting colors to neutral`);
      this.resetColorsToNeutral();
    }

    this.lastEpoch = data.epoch;
    this.currentTrainingData = data;
    this.startAnimation();
  }

  /**
   * Start animation sequence
   */
  private startAnimation(): void {
    this.isAnimating = true;
    this.animationStartTime = performance.now();
    this.isBackpropMode = false;
  }

  /**
   * Animation loop - called via requestAnimationFrame
   */
  private animate = (): void => {
    if (this.isInitialized) {
      this.render();
      if (this.isAnimating) {
        this.processAnimation();
      }
    }
    requestAnimationFrame(this.animate);
  };

  /**
   * Process animation frame
   */
  private processAnimation(): void {
    const currentTime = performance.now();
    const elapsedTime = currentTime - this.animationStartTime;

    if (!this.isBackpropMode) {
      // Forward pass
      if (elapsedTime < this.animationDuration) {
        this.animateForwardPass(elapsedTime / this.animationDuration);
      } else if (elapsedTime < this.animationDuration + this.endAnimationDelay) {
        // Wait phase
        this.animateForwardPass(1.0);
      } else {
        // DISABLED: Skip backprop animation - just stop after forward pass
        // this.isBackpropMode = true;
        // this.backpropStartTime = currentTime;
        this.isAnimating = false;
        this.isWaitingForNextEpoch = true;
      }
    } else {
      // Backprop pass
      const backpropElapsed = currentTime - this.backpropStartTime;
      if (backpropElapsed < this.animationDuration) {
        this.animateBackpropPass(backpropElapsed / this.animationDuration);
      } else {
        this.isAnimating = false;
        this.isWaitingForNextEpoch = true;
      }
    }
  }

  /**
   * Animate forward pass
   */
  private animateForwardPass(progress: number): void {
    if (!this.device || !this.nodeColorBuffer) {
      return;
    }

    const nodeColors = new Float32Array(this.nodeCount * 4);
    const activationValue = this.currentTrainingData?.activations || {};

    // Pre-calculate normalization bounds for each layer - simple linear min/max
    const layerMinMax: Map<string, { min: number; max: number }> = new Map();
    this.layerOrder.forEach((layerName) => {
      const layerActivations = activationValue[layerName] || [];
      if (layerActivations.length > 0) {
        // Simple linear normalization: find actual min and max
        const validValues = layerActivations
          .filter((val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val));
        
        if (validValues.length === 0) {
          layerMinMax.set(layerName, { min: 0, max: 1 });
        } else {
          const min = Math.min(...validValues);
          const max = Math.max(...validValues);
          
          // If all values are the same, use a small range around that value
          if (max === min) {
            layerMinMax.set(layerName, { min: min - 0.1, max: max + 0.1 });
          } else {
            // Simple linear: use actual min and max
            layerMinMax.set(layerName, { min: min, max: max });
          }
        }
      } else {
        layerMinMax.set(layerName, { min: 0, max: 1 });
      }
    });
    
    // Debug: Log normalization values and verify structure matches activations
    if (this.layerOrder.length > 0) {
      const firstLayer = this.layerOrder[0];
      const firstLayerMinMax = layerMinMax.get(firstLayer);
      const firstLayerActivations = activationValue[firstLayer] || [];
      const spatialShape = activationValue[`${firstLayer}_spatial_shape`] as number[] | undefined;
      const layerNodeIndices = this.layerNodeIndices.get(firstLayer) || [];
      
      // Get structure dimensions from current structure
      const structureLayer = this.currentStructure?.layers?.find((l: any) => l.name === firstLayer);
      const structureSpatialDims = structureLayer?.spatial_dims;
      
      const sorted = [...firstLayerActivations].filter((v: any) => typeof v === 'number').sort((a: number, b: number) => a - b);
      const p5 = sorted[Math.floor(sorted.length * 0.05)];
      const p95 = sorted[Math.floor(sorted.length * 0.95)];
      console.log(`[WebGPU] Layer "${firstLayer}" mapping verification:`, {
        structureSpatialDims: structureSpatialDims,
        activationSpatialShape: spatialShape,
        nodeCount: layerNodeIndices.length,
        activationArrayLength: firstLayerActivations.length,
        expectedFromStructure: structureSpatialDims ? structureSpatialDims[0] * structureSpatialDims[1] : 'unknown',
        expectedFromActivation: spatialShape ? (spatialShape.length === 2 ? spatialShape[0] * spatialShape[1] : '3D') : 'unknown',
        match: structureSpatialDims && spatialShape && 
               structureSpatialDims[0] === spatialShape[0] && 
               structureSpatialDims[1] === spatialShape[1],
        percentile5: p5,
        percentile95: p95,
        actualMin: sorted[0],
        actualMax: sorted[sorted.length - 1],
        normalizedMin: firstLayerMinMax?.min,
        normalizedMax: firstLayerMinMax?.max,
        normalizedRange: firstLayerMinMax ? firstLayerMinMax.max - firstLayerMinMax.min : 0,
        sampleValues: firstLayerActivations.slice(0, 10)
      });
    }

    this.nodes.forEach((node, idx) => {
      const layerActivations = activationValue[node.layerName] || [];
      const spatialShape = activationValue[`${node.layerName}_spatial_shape`] as number[] | undefined;
      
      // Get activation value based on layer type and spatial structure
      let activation = 0;
      if (spatialShape && layerActivations.length > 0) {
        // For layers with spatial structure, map node index to spatial position
        if (spatialShape.length === 2) {
          // Input layer: (height, width) - spatial activations
          const [height, width] = spatialShape;
          const row = Math.floor(node.nodeIndex / width);
          const col = node.nodeIndex % width;
          const spatialIdx = row * width + col;
          
          // Debug: Log mapping for first few nodes
          if (idx < 5) {
            console.log(`[WebGPU] Node ${idx} mapping:`, {
              layerName: node.layerName,
              nodeIndex: node.nodeIndex,
              calculatedRow: row,
              calculatedCol: col,
              spatialIdx: spatialIdx,
              activationArrayLength: layerActivations.length,
              spatialShape: spatialShape,
              expectedNodes: height * width,
              activationValue: layerActivations[spatialIdx]
            });
          }
          
          // Ensure we don't go out of bounds
          if (spatialIdx < layerActivations.length) {
            activation = layerActivations[spatialIdx] || 0;
          } else {
            console.warn(`[WebGPU] Spatial index ${spatialIdx} out of bounds for layer ${node.layerName} (length: ${layerActivations.length})`);
          }
        } else if (spatialShape.length === 3) {
          // Conv layer: (channels, height, width)
          // spatialShape is the ACTUAL activation shape (potentially downsampled by trainer)
          // nodeIndex was calculated from STRUCTURE dimensions, which might be larger
          const [actChannels, actHeight, actWidth] = spatialShape;
          
          // Get original structure dimensions for this layer
          const structureLayer = this.layerStructureMap.get(node.layerName);
          const structureDims = structureLayer?.spatial_dims;
          
          if (structureDims && structureDims.length === 3) {
            // Map nodeIndex from structure space to activation space (accounting for downsampling)
            const [structChannels, structHeight, structWidth] = structureDims;
            
            // Calculate which channel/row/col this node represents in STRUCTURE space
            const structCh = Math.floor(node.nodeIndex / (structHeight * structWidth));
            const remainder = node.nodeIndex % (structHeight * structWidth);
            const structRow = Math.floor(remainder / structWidth);
            const structCol = remainder % structWidth;
            
            // Map to ACTIVATION space (accounting for downsampling)
            const actCh = Math.min(structCh, actChannels - 1);
            const actRow = Math.floor((structRow / structHeight) * actHeight);
            const actCol = Math.floor((structCol / structWidth) * actWidth);
            
            // Calculate activation array index
            const spatialIdx = actCh * actHeight * actWidth + actRow * actWidth + actCol;
            
            if (spatialIdx < layerActivations.length) {
              activation = layerActivations[spatialIdx] || 0;
            }
          } else {
            // No structure info available - check if this is a 2D or 3D indexed layer
            const layerNodeIndices = this.layerNodeIndices.get(node.layerName) || [];
            const totalNodesForLayer = layerNodeIndices.length;
            const expected2DNodes = actHeight * actWidth;
            const expected3DNodes = actChannels * actHeight * actWidth;

            const is2DIndexing = Math.abs(totalNodesForLayer - expected2DNodes) < Math.abs(totalNodesForLayer - expected3DNodes);

            if (is2DIndexing && node.nodeIndex < expected2DNodes) {
              // Input_conv layer: average across channels
              const row = Math.floor(node.nodeIndex / actWidth);
              const col = node.nodeIndex % actWidth;
              let sum = 0;
              let count = 0;
              for (let ch = 0; ch < actChannels; ch++) {
                const spatialIdx = ch * actHeight * actWidth + row * actWidth + col;
                if (spatialIdx < layerActivations.length) {
                  sum += layerActivations[spatialIdx] || 0;
                  count++;
                }
              }
              activation = count > 0 ? sum / count : 0;
            } else {
              // Fallback: use nodeIndex directly
              const spatialIdx = node.nodeIndex;
              if (spatialIdx < layerActivations.length) {
                activation = layerActivations[spatialIdx] || 0;
              }
            }
          }
        } else {
          // Fallback: direct index
          if (node.nodeIndex < layerActivations.length) {
            activation = layerActivations[node.nodeIndex] || 0;
          }
        }
      } else {
        // No spatial structure: direct index mapping
        if (node.nodeIndex < layerActivations.length) {
          activation = layerActivations[node.nodeIndex] || 0;
        }
      }

      // Normalize progress to layer
      const layerIndex = this.layerOrder.indexOf(node.layerName);
      const layerProgress = Math.max(
        0,
        Math.min(1, progress * this.layerOrder.length - layerIndex)
      );

      // Normalize activation value to 0-1 range for grayscale using pre-calculated min/max
      const { min: minActivation, max: maxActivation } = layerMinMax.get(node.layerName) || { min: 0, max: 1 };
      const activationRange = maxActivation - minActivation;
      
      // Simple linear normalization - no thresholds, no contrast enhancement, no special curves
      let normalizedActivation = 0;
      if (activationRange > 0.0001) {  // Use small epsilon to avoid division issues
        // Simple linear mapping: (value - min) / (max - min)
        const clampedActivation = Math.max(minActivation, Math.min(maxActivation, activation));
        normalizedActivation = (clampedActivation - minActivation) / activationRange;
      } else if (Math.abs(activation) > 0.0001) {
        // If all activations are the same and non-zero, show as medium gray
        normalizedActivation = 0.5;
      } else {
        // Zero or near-zero activation - show as black
        normalizedActivation = 0.0;
      }
      
      // Clamp to valid range
      normalizedActivation = Math.max(0, Math.min(1, normalizedActivation));
      
      // Always show full activation color - don't multiply by layer progress
      // This ensures nodes maintain their activation-based color and don't blink/disappear
      const grayscaleValue = normalizedActivation;
      
      // Grayscale color: white (1.0) for high activation, black (0.0) for low activation
      // Use same RGB values for grayscale
      const gray = Math.max(0, Math.min(1, grayscaleValue));
      nodeColors[idx * 4] = gray;     // R
      nodeColors[idx * 4 + 1] = gray; // G
      nodeColors[idx * 4 + 2] = gray; // B
      nodeColors[idx * 4 + 3] = 1.0;  // Alpha - always fully visible
    });

    this.device.queue.writeBuffer(
      this.nodeColorBuffer,
      0,
      nodeColors.buffer,
      nodeColors.byteOffset,
      nodeColors.byteLength
    );

    // Update edge colors based on source node activations
    if (this.device && this.edgeColorBuffer && this.edgeCount > 0) {
      const edgeColors = new Float32Array(this.edgeCount * 4);
      
      for (let edgeIdx = 0; edgeIdx < this.edgeCount; edgeIdx++) {
        // Get source node index for this edge
        const sourceNodeIdx = this.edgeToSourceNodeIndex.get(edgeIdx);
        
        if (sourceNodeIdx !== undefined && sourceNodeIdx < this.nodes.length) {
          const sourceNode = this.nodes[sourceNodeIdx];
          const layerActivations = activationValue[sourceNode.layerName] || [];
          const spatialShape = activationValue[`${sourceNode.layerName}_spatial_shape`] as number[] | undefined;
          
          // Get activation value using the same logic as nodes
          let activation = 0;
          if (spatialShape && layerActivations.length > 0) {
            if (spatialShape.length === 2) {
              const [height, width] = spatialShape;
              const row = Math.floor(sourceNode.nodeIndex / width);
              const col = sourceNode.nodeIndex % width;
              const spatialIdx = row * width + col;
              
              if (spatialIdx < layerActivations.length) {
                activation = layerActivations[spatialIdx] || 0;
              }
            } else if (spatialShape.length === 3) {
              const [actChannels, actHeight, actWidth] = spatialShape;
              const structureLayer = this.layerStructureMap.get(sourceNode.layerName);
              const structureDims = structureLayer?.spatial_dims;
              
              if (structureDims && structureDims.length === 3) {
                const [structChannels, structHeight, structWidth] = structureDims;
                const structCh = Math.floor(sourceNode.nodeIndex / (structHeight * structWidth));
                const remainder = sourceNode.nodeIndex % (structHeight * structWidth);
                const structRow = Math.floor(remainder / structWidth);
                const structCol = remainder % structWidth;
                
                const actCh = Math.min(structCh, actChannels - 1);
                const actRow = Math.floor((structRow / structHeight) * actHeight);
                const actCol = Math.floor((structCol / structWidth) * actWidth);
                
                const spatialIdx = actCh * actHeight * actWidth + actRow * actWidth + actCol;
                
                if (spatialIdx < layerActivations.length) {
                  activation = layerActivations[spatialIdx] || 0;
                }
              } else {
                if (sourceNode.nodeIndex < layerActivations.length) {
                  activation = layerActivations[sourceNode.nodeIndex] || 0;
                }
              }
            } else {
              if (sourceNode.nodeIndex < layerActivations.length) {
                activation = layerActivations[sourceNode.nodeIndex] || 0;
              }
            }
          } else {
            if (sourceNode.nodeIndex < layerActivations.length) {
              activation = layerActivations[sourceNode.nodeIndex] || 0;
            }
          }
          
          // Normalize activation using the same min/max as nodes
          const { min: minActivation, max: maxActivation } = layerMinMax.get(sourceNode.layerName) || { min: 0, max: 1 };
          const activationRange = maxActivation - minActivation;
          
          let normalizedActivation = 0;
          if (activationRange > 0.0001) {
            const clampedActivation = Math.max(minActivation, Math.min(maxActivation, activation));
            normalizedActivation = (clampedActivation - minActivation) / activationRange;
          } else if (Math.abs(activation) > 0.0001) {
            normalizedActivation = 0.5;
          } else {
            normalizedActivation = 0.0;
          }
          
          normalizedActivation = Math.max(0, Math.min(1, normalizedActivation));
          
          // White color with brightness based on activation
          const brightness = normalizedActivation;
          edgeColors[edgeIdx * 4] = brightness;     // R
          edgeColors[edgeIdx * 4 + 1] = brightness; // G
          edgeColors[edgeIdx * 4 + 2] = brightness; // B
          edgeColors[edgeIdx * 4 + 3] = 0.8;        // A (keep alpha constant for visibility)
        } else {
          // Fallback: white with low brightness if source node not found
          edgeColors[edgeIdx * 4] = 0.3;
          edgeColors[edgeIdx * 4 + 1] = 0.3;
          edgeColors[edgeIdx * 4 + 2] = 0.3;
          edgeColors[edgeIdx * 4 + 3] = 0.8;
        }
      }
      
      this.device.queue.writeBuffer(
        this.edgeColorBuffer,
        0,
        edgeColors.buffer,
        edgeColors.byteOffset,
        edgeColors.byteLength
      );
    }
  }

  /**
   * Animate backprop pass with weight/bias visualization
   * Blue color scheme: darker blue for positive/larger changes, lighter for negative/smaller changes
   */
  private animateBackpropPass(progress: number): void {
    if (!this.device || !this.nodeColorBuffer) {
      return;
    }

    const nodeColors = new Float32Array(this.nodeCount * 4);
    const reversedLayers = [...this.layerOrder].reverse();

    this.nodes.forEach((node, idx) => {
      const layerIndex = reversedLayers.indexOf(node.layerName);
      const layerProgress = Math.max(
        0,
        Math.min(1, progress * reversedLayers.length - layerIndex)
      );

      // Get weight change for this layer if available
      let weightValue = 0.5; // Default: neutral gray (no change)
      let hasWeightData = false;

      if (this.weightChanges.has(node.layerName)) {
        hasWeightData = true;
        const changeData = this.weightChanges.get(node.layerName)!;

        // Map node index to weight array
        // For linear layers: direct index
        // For conv layers: need to handle spatial structure
        let weightIdx = node.nodeIndex;

        if (this.showWeightDelta) {
          // Show normalized delta: 0.5 = no change, 0-0.5 = negative change (lighter), 0.5-1.0 = positive change (darker)
          const deltaArray = changeData.delta_normalized;
          if (weightIdx < deltaArray.length) {
            // Remap from -1..1 to 0..1
            weightValue = 0.5 + deltaArray[weightIdx] * 0.5;
          }
        } else {
          // Show absolute delta magnitude
          const deltaArray = changeData.delta;
          if (weightIdx < deltaArray.length) {
            const absDelta = Math.abs(deltaArray[weightIdx]);
            const maxDelta = Math.max(Math.abs(changeData.delta_min), Math.abs(changeData.delta_max));
            if (maxDelta > 1e-8) {
              weightValue = absDelta / maxDelta; // 0 to 1
            } else {
              weightValue = 0.5;
            }
          }
        }
      }

      // Apply blue color scheme based on weight change
      // Lighter blue (high saturation, low value): 0 to 0.5 (negative/low changes)
      // Darker blue (high saturation, high value): 0.5 to 1.0 (positive/high changes)
      const hue = 200; // Blue hue in HSL

      // For visualization: use HSL to Blue color space
      // weightValue 0 = light blue (low activation), weightValue 1 = dark blue (high activation)
      let r, g, b;

      if (weightValue < 0.5) {
        // Light blue range (0 to 0.5)
        const t = weightValue * 2; // 0 to 1
        // Light blue: RGB(173, 216, 230) to RGB(0, 102, 204)
        r = 173 * (1 - t) + 0 * t;
        g = 216 * (1 - t) + 102 * t;
        b = 230 * (1 - t) + 204 * t;
      } else {
        // Dark blue range (0.5 to 1.0)
        const t = (weightValue - 0.5) * 2; // 0 to 1
        // Dark blue: RGB(0, 102, 204) to RGB(0, 51, 153)
        r = 0 * (1 - t) + 0 * t;
        g = 102 * (1 - t) + 51 * t;
        b = 204 * (1 - t) + 153 * t;
      }

      // Normalize to 0-1 range and apply layer progress alpha
      nodeColors[idx * 4] = (r / 255) * layerProgress;     // R
      nodeColors[idx * 4 + 1] = (g / 255) * layerProgress; // G
      nodeColors[idx * 4 + 2] = (b / 255) * layerProgress; // B
      nodeColors[idx * 4 + 3] = layerProgress * 0.8;       // A (alpha)
    });

    this.device.queue.writeBuffer(
      this.nodeColorBuffer,
      0,
      nodeColors.buffer,
      nodeColors.byteOffset,
      nodeColors.byteLength
    );
  }

  /**
   * Render frame
   */
  private render(): void {
    if (
      !this.device ||
      !this.context ||
      !this.canvas ||
      !this.nodePipeline ||
      !this.nodeVertexBuffer ||
      !this.uniformBuffer ||
      !this.depthTexture ||
      !this.bindGroup
    ) {
      // Don't render if buffers are not ready (e.g., during structure update)
      return;
    }

    // Check canvas size - don't render if size is zero
    if (this.canvas.width === 0 || this.canvas.height === 0) {
      return;
    }

    // Edge buffers are optional (only needed in 3D depth mode)
    // If edges exist, they should be present, but we check before using them

    // Update uniform buffer with matrices
    this.updateUniformBuffer();

    const commandEncoder = this.device.createCommandEncoder();
    const canvasTexture = this.context.getCurrentTexture();

    const renderPass = commandEncoder.beginRenderPass({
      label: "render pass",
      colorAttachments: [
        {
          view: canvasTexture.createView(),
          clearValue: { r: 0.1, g: 0.1, b: 0.1, a: 1.0 },
          loadOp: "clear" as GPULoadOp,
          storeOp: "store" as GPUStoreOp,
        },
      ] as GPURenderPassColorAttachment[],
      depthStencilAttachment: {
        view: this.depthTexture.createView(),
        depthClearValue: 1.0,
        depthLoadOp: "clear" as GPULoadOp,
        depthStoreOp: "store" as GPUStoreOp,
      },
    });

    // Render edges first (so they appear behind nodes) - only if edges exist and showEdges is true
    if (this.showEdges && this.edgeCount > 0 && this.edgePipeline && this.edgeBindGroup && this.edgeVertexBuffer) {
      renderPass.setPipeline(this.edgePipeline);
      renderPass.setBindGroup(0, this.edgeBindGroup);
      renderPass.setVertexBuffer(0, this.edgeVertexBuffer);
      renderPass.draw(this.edgeVertexCount);
    }

    // Render nodes
    renderPass.setPipeline(this.nodePipeline);
    renderPass.setBindGroup(0, this.bindGroup!);
    renderPass.setVertexBuffer(0, this.nodeVertexBuffer);
    renderPass.setIndexBuffer(this.nodeIndexBuffer!, "uint32");
    renderPass.drawIndexed(this.nodeIndexCount, this.nodeCount, 0, 0, 0);

    renderPass.end();
    this.device.queue.submit([commandEncoder.finish()]);
  }

  /**
   * Update uniform buffer with view and projection matrices
   */
  private updateUniformBuffer(): void {
    if (!this.device || !this.uniformBuffer || !this.canvas) {
      return;
    }

    // Create projection matrix (perspective)
    const aspect = this.canvas.width / this.canvas.height;
    const fov = (this.camera.fov * Math.PI) / 180;
    const projMatrix = this.perspective(fov, aspect, this.camera.near, this.camera.far);

    // Create view matrix using camera position and orientation
    // Calculate forward direction from pitch and yaw
    const forwardX = Math.sin(this.camera.yaw) * Math.cos(this.camera.pitch);
    const forwardY = Math.sin(this.camera.pitch);
    const forwardZ = Math.cos(this.camera.yaw) * Math.cos(this.camera.pitch);
    
    // Calculate target point based on camera position and forward direction
    const targetDistance = 100; // Distance to look ahead
    const target = {
      x: this.camera.position.x + forwardX * targetDistance,
      y: this.camera.position.y + forwardY * targetDistance,
      z: this.camera.position.z + forwardZ * targetDistance,
    };
    
    // Calculate up vector (perpendicular to forward)
    const up = {
      x: -Math.sin(this.camera.yaw) * Math.sin(this.camera.pitch),
      y: Math.cos(this.camera.pitch),
      z: -Math.cos(this.camera.yaw) * Math.sin(this.camera.pitch),
    };
    
    const viewMatrix = this.lookAt(this.camera.position, target, up);

    // Combine into uniform buffer
    const uniformData = new Float32Array(32); // 16 for proj + 16 for view
    uniformData.set(projMatrix, 0);
    uniformData.set(viewMatrix, 16);

    this.device.queue.writeBuffer(this.uniformBuffer, 0, uniformData);
  }

  /**
   * Create perspective projection matrix
   */
  private perspective(
    fov: number,
    aspect: number,
    near: number,
    far: number
  ): Float32Array {
    const f = 1.0 / Math.tan(fov / 2);
    const nf = 1 / (near - far);
    return new Float32Array([
      f / aspect, 0, 0, 0,
      0, f, 0, 0,
      0, 0, (far + near) * nf, -1,
      0, 0, 2 * far * near * nf, 0,
    ]);
  }

  /**
   * Create lookAt view matrix
   */
  private lookAt(
    eye: { x: number; y: number; z: number },
    target: { x: number; y: number; z: number },
    up: { x: number; y: number; z: number }
  ): Float32Array {
    const z = this.normalize({
      x: eye.x - target.x,
      y: eye.y - target.y,
      z: eye.z - target.z,
    });
    const x = this.normalize(this.cross(up, z));
    const y = this.cross(z, x);

    return new Float32Array([
      x.x, y.x, z.x, 0,
      x.y, y.y, z.y, 0,
      x.z, y.z, z.z, 0,
      -this.dot(x, eye), -this.dot(y, eye), -this.dot(z, eye), 1,
    ]);
  }

  /**
   * Vector normalize
   */
  private normalize(v: { x: number; y: number; z: number }): {
    x: number;
    y: number;
    z: number;
  } {
    const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    return { x: v.x / len, y: v.y / len, z: v.z / len };
  }

  /**
   * Vector cross product
   */
  private cross(
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number }
  ): { x: number; y: number; z: number } {
    return {
      x: a.y * b.z - a.z * b.y,
      y: a.z * b.x - a.x * b.z,
      z: a.x * b.y - a.y * b.x,
    };
  }

  /**
   * Vector dot product
   */
  private dot(
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number }
  ): number {
    return a.x * b.x + a.y * b.y + a.z * b.z;
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    if (!this.canvas) return;

    this.canvas.addEventListener("mousedown", this.onMouseDown);
    this.canvas.addEventListener("mousemove", this.onMouseMove);
    this.canvas.addEventListener("mouseup", this.onMouseUp);
    this.canvas.addEventListener("mouseleave", this.onMouseUp);
    this.canvas.addEventListener("wheel", this.onMouseWheel);
    // Prevent context menu on right-click
    this.canvas.addEventListener("contextmenu", (e) => e.preventDefault());

    window.addEventListener("resize", this.handleResize);
  }

  /**
   * Mouse down handler
   */
  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) {
      // Left mouse button: rotation
      this.isDragging = true;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    } else if (e.button === 2) {
      // Right mouse button: panning/repositioning
      this.isRightDragging = true;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    }
  };

  /**
   * Mouse move handler
   */
  private onMouseMove = (e: MouseEvent): void => {
    if (this.isDragging) {
      // Left-click drag: rotate camera around its own axis (FPS-style)
      const deltaX = e.clientX - this.lastMouseX;
      const deltaY = e.clientY - this.lastMouseY;

      // Update camera orientation
      this.camera.yaw += deltaX * 0.005;
      this.camera.pitch += deltaY * 0.005;

      // Clamp pitch to prevent flipping
      this.camera.pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.camera.pitch));

      // Update rotation for backward compatibility with view matrix calculation
      this.camera.rotationY = this.camera.yaw;
      this.camera.rotationX = this.camera.pitch;

      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    } else if (this.isRightDragging) {
      // Right-click drag: move camera position in 3D space
      const deltaX = e.clientX - this.lastMouseX;
      const deltaY = e.clientY - this.lastMouseY;

      // Calculate movement speed (scale with zoom)
      const moveSpeed = 500 / this.zoomLevel * 0.001;
      
      // Calculate camera's forward, right, and up vectors
      const forwardX = Math.sin(this.camera.yaw) * Math.cos(this.camera.pitch);
      const forwardY = Math.sin(this.camera.pitch);
      const forwardZ = Math.cos(this.camera.yaw) * Math.cos(this.camera.pitch);
      
      const rightX = Math.cos(this.camera.yaw);
      const rightZ = -Math.sin(this.camera.yaw);
      
      const upX = -Math.sin(this.camera.yaw) * Math.sin(this.camera.pitch);
      const upY = Math.cos(this.camera.pitch);
      const upZ = -Math.cos(this.camera.yaw) * Math.sin(this.camera.pitch);

      // Move camera position in camera-relative space
      // Right movement
      this.camera.position.x += deltaX * moveSpeed * rightX;
      this.camera.position.z += deltaX * moveSpeed * rightZ;
      
      // Up/down movement (using camera's up vector)
      this.camera.position.x += deltaY * moveSpeed * upX;
      this.camera.position.y += deltaY * moveSpeed * upY;
      this.camera.position.z += deltaY * moveSpeed * upZ;
      
      // Update target to maintain view direction
      this.camera.target.x = this.camera.position.x + forwardX * 100;
      this.camera.target.y = this.camera.position.y + forwardY * 100;
      this.camera.target.z = this.camera.position.z + forwardZ * 100;

      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    }
  };

  /**
   * Mouse up handler
   */
  private onMouseUp = (): void => {
    this.isDragging = false;
    this.isRightDragging = false;
  };

  /**
   * Mouse wheel handler (zoom by moving camera forward/backward)
   */
  private onMouseWheel = (e: WheelEvent): void => {
    e.preventDefault();
    
    // Calculate forward direction from camera orientation
    const forwardX = Math.sin(this.camera.yaw) * Math.cos(this.camera.pitch);
    const forwardY = Math.sin(this.camera.pitch);
    const forwardZ = Math.cos(this.camera.yaw) * Math.cos(this.camera.pitch);
    
    // Move camera forward (zoom in) or backward (zoom out) along view direction
    const zoomSpeed = 10; // Speed of zoom movement
    const zoomDirection = e.deltaY > 0 ? -1 : 1; // Negative deltaY = scroll up = zoom in
    
    this.camera.position.x += zoomDirection * zoomSpeed * forwardX;
    this.camera.position.y += zoomDirection * zoomSpeed * forwardY;
    this.camera.position.z += zoomDirection * zoomSpeed * forwardZ;
    
    // Update zoom level for compatibility (used in movement speed calculations)
    // Estimate zoom level based on distance from origin
    const distance = Math.sqrt(
      this.camera.position.x ** 2 + 
      this.camera.position.y ** 2 + 
      this.camera.position.z ** 2
    );
    this.zoomLevel = Math.max(0.1, Math.min(3.0, 500 / distance));
  };

  /**
   * Handle window resize
   */
  public handleResize = (): void => {
    if (!this.canvas || !this.device) return;

    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.canvas.width = width;
    this.canvas.height = height;

    // Recreate depth texture to match new canvas size (only if size is valid)
    if (this.depthTexture) {
      this.depthTexture.destroy();
    }
    
    if (this.canvas.width > 0 && this.canvas.height > 0) {
      this.depthTexture = this.device.createTexture({
        size: [this.canvas.width, this.canvas.height],
        format: "depth24plus",
        usage: GPUTextureUsage.RENDER_ATTACHMENT,
      });
    } else {
      this.depthTexture = null;
    }
  };

  /**
   * Destroy GPU buffers and textures (internal method)
   * @param preserveColorBuffers - If true, preserve color buffers to avoid flicker
   */
  private destroyBuffers(preserveColorBuffers: boolean = false): void {
    // Destroy buffers if they exist
    if (this.nodeVertexBuffer) {
      this.nodeVertexBuffer.destroy();
      this.nodeVertexBuffer = null;
    }
    if (this.nodeIndexBuffer) {
      this.nodeIndexBuffer.destroy();
      this.nodeIndexBuffer = null;
    }
    if (this.edgeVertexBuffer) {
      this.edgeVertexBuffer.destroy();
      this.edgeVertexBuffer = null;
    }

    // Conditionally preserve color buffers to avoid flickering between epochs
    if (!preserveColorBuffers) {
      if (this.nodeColorBuffer) {
        this.nodeColorBuffer.destroy();
        this.nodeColorBuffer = null;
      }
      if (this.edgeColorBuffer) {
        this.edgeColorBuffer.destroy();
        this.edgeColorBuffer = null;
      }
    }

    if (this.nodePositionBuffer) {
      this.nodePositionBuffer.destroy();
      this.nodePositionBuffer = null;
    }
    if (this.uniformBuffer) {
      this.uniformBuffer.destroy();
      this.uniformBuffer = null;
    }
    if (this.depthTexture) {
      this.depthTexture.destroy();
      this.depthTexture = null;
    }

    // Only reset bind groups if color buffers were destroyed
    // If color buffers are preserved, bind groups might still be valid
    if (!preserveColorBuffers) {
      this.bindGroup = null;
      this.edgeBindGroup = null;
    }
  }

  /**
   * Update node colors directly based on activation values
   * Used for batch updates - NO animation, NO progress interpolation
   * Colors update immediately without triggering animation sequences
   */
  public updateNodeColorsDirectly(activations: LayerActivations): void {
    if (!this.device || !this.nodeColorBuffer || this.nodeCount === 0) {
      return;
    }

    const nodeColors = new Float32Array(this.nodeCount * 4);

    // Pre-calculate normalization bounds for each layer
    const layerMinMax: Map<string, { min: number; max: number }> = new Map();
    this.layerOrder.forEach((layerName) => {
      const layerActivations = activations[layerName] || [];
      if (layerActivations.length > 0) {
        const validValues = layerActivations
          .filter((val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val));

        if (validValues.length === 0) {
          layerMinMax.set(layerName, { min: 0, max: 1 });
        } else {
          const min = Math.min(...validValues);
          const max = Math.max(...validValues);

          if (max === min) {
            layerMinMax.set(layerName, { min: min - 0.1, max: max + 0.1 });
          } else {
            layerMinMax.set(layerName, { min: min, max: max });
          }
        }
      } else {
        layerMinMax.set(layerName, { min: 0, max: 1 });
      }
    });

    // Update colors for all nodes
    this.nodes.forEach((node, idx) => {
      const layerActivations = activations[node.layerName] || [];
      const spatialShape = activations[`${node.layerName}_spatial_shape`] as number[] | undefined;

      // Get activation value based on layer type and spatial structure
      let activation = 0;
      if (spatialShape && layerActivations.length > 0) {
        if (spatialShape.length === 2) {
          // Input layer: (height, width) - spatial activations
          const [height, width] = spatialShape;
          const row = Math.floor(node.nodeIndex / width);
          const col = node.nodeIndex % width;
          const spatialIdx = row * width + col;

          if (spatialIdx < layerActivations.length) {
            activation = layerActivations[spatialIdx] || 0;
          }
        } else if (spatialShape.length === 3) {
          // Conv layer: (channels, height, width)
          // spatialShape is the ACTUAL activation shape (potentially downsampled by trainer)
          // nodeIndex was calculated from STRUCTURE dimensions, which might be larger
          const [actChannels, actHeight, actWidth] = spatialShape;
          
          // Get original structure dimensions for this layer
          const structureLayer = this.layerStructureMap.get(node.layerName);
          const structureDims = structureLayer?.spatial_dims;
          
          if (structureDims && structureDims.length === 3) {
            // Map nodeIndex from structure space to activation space (accounting for downsampling)
            const [structChannels, structHeight, structWidth] = structureDims;
            
            // Calculate which channel/row/col this node represents in STRUCTURE space
            const structCh = Math.floor(node.nodeIndex / (structHeight * structWidth));
            const remainder = node.nodeIndex % (structHeight * structWidth);
            const structRow = Math.floor(remainder / structWidth);
            const structCol = remainder % structWidth;
            
            // Map to ACTIVATION space (accounting for downsampling)
            const actCh = Math.min(structCh, actChannels - 1);
            const actRow = Math.floor((structRow / structHeight) * actHeight);
            const actCol = Math.floor((structCol / structWidth) * actWidth);
            
            // Calculate activation array index
            const spatialIdx = actCh * actHeight * actWidth + actRow * actWidth + actCol;
            
            if (spatialIdx < layerActivations.length) {
              activation = layerActivations[spatialIdx] || 0;
            }
          } else {
            // No structure info available - check if this is a 2D or 3D indexed layer
            const layerNodeIndices = this.layerNodeIndices.get(node.layerName) || [];
            const totalNodesForLayer = layerNodeIndices.length;
            const expected2DNodes = actHeight * actWidth;
            const expected3DNodes = actChannels * actHeight * actWidth;

            const is2DIndexing = Math.abs(totalNodesForLayer - expected2DNodes) < Math.abs(totalNodesForLayer - expected3DNodes);

            if (is2DIndexing && node.nodeIndex < expected2DNodes) {
              // Input_conv layer: average across channels
              const row = Math.floor(node.nodeIndex / actWidth);
              const col = node.nodeIndex % actWidth;
              let sum = 0;
              let count = 0;
              for (let ch = 0; ch < actChannels; ch++) {
                const spatialIdx = ch * actHeight * actWidth + row * actWidth + col;
                if (spatialIdx < layerActivations.length) {
                  sum += layerActivations[spatialIdx] || 0;
                  count++;
                }
              }
              activation = count > 0 ? sum / count : 0;
            } else {
              // Fallback: use nodeIndex directly (might be out of bounds if downsampled)
              const spatialIdx = node.nodeIndex;
              if (spatialIdx < layerActivations.length) {
                activation = layerActivations[spatialIdx] || 0;
              }
            }
          }
        } else {
          // Fallback: direct index
          if (node.nodeIndex < layerActivations.length) {
            activation = layerActivations[node.nodeIndex] || 0;
          }
        }
      } else {
        // No spatial structure: direct index mapping
        if (node.nodeIndex < layerActivations.length) {
          activation = layerActivations[node.nodeIndex] || 0;
        }
      }

      // Normalize activation value to 0-1 range for grayscale
      const { min: minActivation, max: maxActivation } = layerMinMax.get(node.layerName) || { min: 0, max: 1 };
      const activationRange = maxActivation - minActivation;

      let normalizedActivation = 0;
      if (activationRange > 0.0001) {
        const clampedActivation = Math.max(minActivation, Math.min(maxActivation, activation));
        normalizedActivation = (clampedActivation - minActivation) / activationRange;
      } else if (Math.abs(activation) > 0.0001) {
        normalizedActivation = 0.5;
      } else {
        normalizedActivation = 0.0;
      }

      // Clamp to valid range
      normalizedActivation = Math.max(0, Math.min(1, normalizedActivation));

      // Set grayscale color
      const gray = normalizedActivation;
      nodeColors[idx * 4] = gray;     // R
      nodeColors[idx * 4 + 1] = gray; // G
      nodeColors[idx * 4 + 2] = gray; // B
      nodeColors[idx * 4 + 3] = 1.0;  // A
    });

    // Write colors to GPU buffer
    this.device.queue.writeBuffer(
      this.nodeColorBuffer,
      0,
      nodeColors.buffer,
      nodeColors.byteOffset,
      nodeColors.byteLength
    );

    // Update edge colors based on source node activations
    if (this.device && this.edgeColorBuffer && this.edgeCount > 0) {
      const edgeColors = new Float32Array(this.edgeCount * 4);
      
      // Pre-calculate normalization bounds for each layer (same as nodes)
      const layerMinMax: Map<string, { min: number; max: number }> = new Map();
      this.layerOrder.forEach((layerName) => {
        const layerActivations = activations[layerName] || [];
        if (layerActivations.length > 0) {
          const validValues = layerActivations
            .filter((val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val));

          if (validValues.length === 0) {
            layerMinMax.set(layerName, { min: 0, max: 1 });
          } else {
            const min = Math.min(...validValues);
            const max = Math.max(...validValues);

            if (max === min) {
              layerMinMax.set(layerName, { min: min - 0.1, max: max + 0.1 });
            } else {
              layerMinMax.set(layerName, { min: min, max: max });
            }
          }
        } else {
          layerMinMax.set(layerName, { min: 0, max: 1 });
        }
      });
      
      for (let edgeIdx = 0; edgeIdx < this.edgeCount; edgeIdx++) {
        const sourceNodeIdx = this.edgeToSourceNodeIndex.get(edgeIdx);
        
        if (sourceNodeIdx !== undefined && sourceNodeIdx < this.nodes.length) {
          const sourceNode = this.nodes[sourceNodeIdx];
          const layerActivations = activations[sourceNode.layerName] || [];
          const spatialShape = activations[`${sourceNode.layerName}_spatial_shape`] as number[] | undefined;
          
          // Get activation value using the same logic as nodes
          let activation = 0;
          if (spatialShape && layerActivations.length > 0) {
            if (spatialShape.length === 2) {
              const [height, width] = spatialShape;
              const row = Math.floor(sourceNode.nodeIndex / width);
              const col = sourceNode.nodeIndex % width;
              const spatialIdx = row * width + col;
              
              if (spatialIdx < layerActivations.length) {
                activation = layerActivations[spatialIdx] || 0;
              }
            } else if (spatialShape.length === 3) {
              const [actChannels, actHeight, actWidth] = spatialShape;
              const structureLayer = this.layerStructureMap.get(sourceNode.layerName);
              const structureDims = structureLayer?.spatial_dims;
              
              if (structureDims && structureDims.length === 3) {
                const [structChannels, structHeight, structWidth] = structureDims;
                const structCh = Math.floor(sourceNode.nodeIndex / (structHeight * structWidth));
                const remainder = sourceNode.nodeIndex % (structHeight * structWidth);
                const structRow = Math.floor(remainder / structWidth);
                const structCol = remainder % structWidth;
                
                const actCh = Math.min(structCh, actChannels - 1);
                const actRow = Math.floor((structRow / structHeight) * actHeight);
                const actCol = Math.floor((structCol / structWidth) * actWidth);
                
                const spatialIdx = actCh * actHeight * actWidth + actRow * actWidth + actCol;
                
                if (spatialIdx < layerActivations.length) {
                  activation = layerActivations[spatialIdx] || 0;
                }
              } else {
                if (sourceNode.nodeIndex < layerActivations.length) {
                  activation = layerActivations[sourceNode.nodeIndex] || 0;
                }
              }
            } else {
              if (sourceNode.nodeIndex < layerActivations.length) {
                activation = layerActivations[sourceNode.nodeIndex] || 0;
              }
            }
          } else {
            if (sourceNode.nodeIndex < layerActivations.length) {
              activation = layerActivations[sourceNode.nodeIndex] || 0;
            }
          }
          
          // Normalize activation using the same min/max as nodes
          const { min: minActivation, max: maxActivation } = layerMinMax.get(sourceNode.layerName) || { min: 0, max: 1 };
          const activationRange = maxActivation - minActivation;
          
          let normalizedActivation = 0;
          if (activationRange > 0.0001) {
            const clampedActivation = Math.max(minActivation, Math.min(maxActivation, activation));
            normalizedActivation = (clampedActivation - minActivation) / activationRange;
          } else if (Math.abs(activation) > 0.0001) {
            normalizedActivation = 0.5;
          } else {
            normalizedActivation = 0.0;
          }
          
          normalizedActivation = Math.max(0, Math.min(1, normalizedActivation));
          
          // White color with brightness based on activation
          const brightness = normalizedActivation;
          edgeColors[edgeIdx * 4] = brightness;     // R
          edgeColors[edgeIdx * 4 + 1] = brightness; // G
          edgeColors[edgeIdx * 4 + 2] = brightness; // B
          edgeColors[edgeIdx * 4 + 3] = 0.8;        // A
        } else {
          // Fallback: white with low brightness if source node not found
          edgeColors[edgeIdx * 4] = 0.3;
          edgeColors[edgeIdx * 4 + 1] = 0.3;
          edgeColors[edgeIdx * 4 + 2] = 0.3;
          edgeColors[edgeIdx * 4 + 3] = 0.8;
        }
      }
      
      this.device.queue.writeBuffer(
        this.edgeColorBuffer,
        0,
        edgeColors.buffer,
        edgeColors.byteOffset,
        edgeColors.byteLength
      );
    }
  }

  /**
   * Reset all node colors to neutral gray
   * Used for transitions between epochs to provide visual continuity
   */
  private resetColorsToNeutral(): void {
    if (!this.device || !this.nodeColorBuffer || this.nodeCount === 0) {
      return;
    }

    const nodeColors = new Float32Array(this.nodeCount * 4);
    for (let i = 0; i < this.nodeCount; i++) {
      nodeColors[i * 4] = 0.33;     // R
      nodeColors[i * 4 + 1] = 0.33; // G
      nodeColors[i * 4 + 2] = 0.33; // B
      nodeColors[i * 4 + 3] = 1.0;  // A
    }

    this.device.queue.writeBuffer(
      this.nodeColorBuffer,
      0,
      nodeColors.buffer,
      nodeColors.byteOffset,
      nodeColors.byteLength
    );

    // Reset edge colors to neutral white
    if (this.edgeColorBuffer && this.edgeCount > 0) {
      const edgeColors = new Float32Array(this.edgeCount * 4);
      for (let i = 0; i < this.edgeCount; i++) {
        edgeColors[i * 4] = 0.5;     // R (neutral white brightness)
        edgeColors[i * 4 + 1] = 0.5; // G
        edgeColors[i * 4 + 2] = 0.5; // B
        edgeColors[i * 4 + 3] = 0.8;  // A
      }

      this.device.queue.writeBuffer(
        this.edgeColorBuffer,
        0,
        edgeColors.buffer,
        edgeColors.byteOffset,
        edgeColors.byteLength
      );
    }
  }

  /**
   * Clean up resources
   */
  public cleanup(): void {
    this.destroyBuffers();

    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}
